import posthog from "posthog-js";
import { writable } from "svelte/store";
import { getUserID } from "./user";
import { isDev, len, throwIf, timeSince } from "./util";

var enc = encodeURIComponent;

export interface HttpProgress {
  (size: number, totalSize: number, durMs: number): void;
}

export async function getAsBlob(url: string, onProgress: HttpProgress): Promise<Blob> {
  const startTime: Date = new Date();
  const rsp: Response = await fetch(url);
  const r: ReadableStreamDefaultReader<Uint8Array> = rsp.body!.getReader();
  const totalSize: number = +(rsp.headers.get("Content-Length") as string);
  let size: number = 0;
  let chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await r.read();
    if (done) {
      break;
    }
    chunks.push(value!);
    size += value!.length;
    const durMs: number = timeSince(startTime);
    onProgress(size, totalSize, durMs);
  }
  const blob: Blob = new Blob(chunks);
  return blob;
}

export async function getAsText(url: string, onProgress: HttpProgress): Promise<string> {
  const blob = await getAsBlob(url, onProgress);
  const dec = new TextDecoder("utf-8");
  return dec.decode(await blob.arrayBuffer());
}

export async function getAsJSON(url: string, onProgress: HttpProgress): Promise<object> {
  const s = await getAsText(url, onProgress);
  return JSON.parse(s);
}

// TODO: seems hackish. X-User is only for identifying user when talking to
// our backend. It breaks CORS when used with e.g. dropbox urls
function makeFetchOpts(uri: string) {
  let userID = getUserID();
  if (!userID) {
    return {};
  }
  let apiCall = uri.startsWith("/api/");
  if (!apiCall) {
    return {};
  }
  return {
    headers: {
      "X-User": userID,
    },
  };
}

export async function apiFetch(uri: string): Promise<Response> {
  let opts = makeFetchOpts(uri);
  return fetch(uri, opts);
}

/**
 * Note: not async because we don't care
 */
export function telegramNotify(msg: string) {
  if (isDev()) {
    return;
  }
  const uri = `/api/telegram/notify?msg=${enc(msg)}`;
  apiFetch(uri);
}

/**
 * abstracts capturing events and also only needs to silence
 * warning about posthog. once
 */
export function captureEvent(name: string, arg: any) {
  let url = "/api/le?name=" + enc(name);
  if (arg) {
    for (let k of Object.keys(arg)) {
      url = url + `&${enc(k)}=${enc(arg[k])}`;
    }
  }
  apiFetch(url);

  if (isDev()) {
    // don't capture events from my dev usage
    return;
  }
  posthog.capture(name, arg);
}

export function fetchStore(url: string, onProgress: HttpProgress) {
  const loading = writable(false);
  const error = writable("");
  const data = writable({});

  async function get() {
    loading.set(true);
    error.set("");
    try {
      const s = await getAsText(url, onProgress);
      data.set(s);
    } catch (e: any) {
      let msg = `Fetching ${url} failed with ${e.toString()}`;
      error.set(msg);
    }
    loading.set(false);
  }

  get();

  return [data, loading, error];
}

export async function puthWithFetch(uri: string, file: File) {
  let size = file.size;
  console.log(`puthWithFetch: uri=${uri}, size=${size}`);
  let rsp = await fetch(uri, {
    method: "PUT",
    // mode: "no-cors",
    headers: {
      // "Access-Control-Allow-Origin": "*", // Required for CORS support to work
      // "Access-Control-Allow-Credentials": true, // Required for cookies, authorization headers with HTTPS
      // doesn't support chunked encoding so must provide size
      // "Content-Length": size,
    },
    body: file,
    // body: body,
  });
  await throwIfFetchFailed(rsp);
  let js = await rsp.json();
  console.log(`puthWithFetch: '${js}'`);
  return js;
}

export async function throwIfFetchFailed(rsp: Response) {
  if (rsp.status >= 200 && rsp.status < 300) {
    return;
  }
  //let s = await rsp.text();
  //let s = await rsp.json();
  //console.log("s:", s);
  const msg = `${rsp.url} failed with ${rsp.status} ${rsp.statusText}.`;
  throw new Error(msg);
}

export class XhrOpts {
  method = "GET";
  params?: string | object | FormData;
  data?: Blob | string;
  responseType: XMLHttpRequestResponseType = "";
  headers: Record<string, string> = {};
  httpProgress?: HttpProgress;
}

/**
 * Native XMLHttpRequest as Promise
 * @see https://stackoverflow.com/a/30008115/933951
 * @see https://javascript.info/xmlhttprequest
 */
export function xhrAsync(url: string, opts: XhrOpts): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    let params = opts.params;

    let lcMethod = opts.method.toLowerCase();

    let paramsInURL = lcMethod == "get" || lcMethod == "head" || lcMethod == "delete";
    // We'll need to stringify if we've been given an object
    // If we have a string, this is skipped.

    if (params && typeof params === `object` && !(params instanceof FormData)) {
      let a = [];
      for (let [k, v] of Object.entries(params)) {
        a.push(`${enc(k)}=${enc(v)}`);
      }
      params = a.join(`&`);
    }
    if (paramsInURL && len(params) > 0) {
      url = url + "?" + params;
      params = undefined;
    }

    xhr.open(opts.method, url);

    xhr.responseType = opts.responseType;
    if (xhr.responseType == "" && lcMethod == "get") {
      xhr.responseType = "blob";
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
      } else {
        reject({
          status: xhr.status,
          statusText: xhr.statusText,
        });
      }
    };

    xhr.onerror = () => {
      reject({
        status: xhr.status,
        statusText: xhr.statusText,
      });
    };

    if (opts.headers) {
      Object.keys(opts.headers).forEach((key) => {
        xhr.setRequestHeader(key, opts.headers[key]);
      });
    }

    const startTime = new Date();

    function sendProgress(ev: ProgressEvent<EventTarget>, onProgress: HttpProgress | undefined) {
      console.log("sendProgress:", ev);
      if (!onProgress) {
        return;
      }
      const size = ev.loaded;
      let totalSize = 0;
      if (ev.lengthComputable) {
        totalSize = ev.total;
      }
      const durMs = timeSince(startTime);
      onProgress(size, totalSize, durMs);
    }

    if (opts.method == "PUT" || opts.method == "POST" || opts.method == "PATCH") {
      xhr.upload.onprogress = function (ev) {
        // console.log("xhr.upload.onprogress:", ev);
        sendProgress(ev, opts.httpProgress);
      };
    } else {
      xhr.onprogress = function (ev) {
        // console.log("xhr.onprogress:", ev);
        sendProgress(ev, opts.httpProgress);
      };
    }

    if (opts.data) {
      xhr.send(opts.data);
    } else if (params) {
      xhr.send(params);
    } else {
      xhr.send();
    }
  });
}

export async function xhrGetAsBlob(uri: string, httpProgress: HttpProgress): Promise<Blob> {
  let opts = new XhrOpts();
  opts.httpProgress = httpProgress;
  opts.responseType = "blob";
  const res = await xhrAsync(uri, opts);
  return res.response;
}

export async function xhrGetJSONAuth(url: string, accessToken: string, params: object = {}): Promise<object> {
  let opts = new XhrOpts();
  opts.params = params;
  opts.responseType = "json";
  opts.headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  let xhr = await xhrAsync(url, opts);
  throwIfXhrFailed(xhr);
  return xhr.response;
}

export async function xhrDeleteAuth(url: string, accessToken: string, params: object = {}): Promise<object> {
  let opts = new XhrOpts();
  opts.method = "DELETE";
  opts.params = params;
  opts.responseType = "json";
  opts.headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  let xhr = await xhrAsync(url, opts);
  throwIfXhrFailed(xhr);
  return xhr.response;
}

export async function xhrPost(uri: string, httpProgress: HttpProgress): Promise<any> {
  let opts = new XhrOpts();
  opts.method = "POST";
  opts.httpProgress = httpProgress;
  const xhr = await xhrAsync(uri, opts);
  return xhr.response;
}

export async function xhrPostAsJSON(uri: string, params: object, httpProgress: HttpProgress): Promise<object> {
  let opts = new XhrOpts();
  opts.method = "POST";
  let fd = new FormData();
  for (let [k, v] of Object.entries(params)) {
    fd.set(k, v);
  }

  opts.params = params;
  opts.responseType = "json";
  opts.headers = {
    "Content-Type": "application/x-www-form-urlencoded",
  };
  opts.httpProgress = httpProgress;
  const xhr = await xhrAsync(uri, opts);
  return xhr.response;
}

export async function xhrPut(
  uri: string,
  body: Blob,
  httpProgress: HttpProgress,
  accessToken: string | null = null,
): Promise<XMLHttpRequest> {
  let opts = new XhrOpts();
  opts.method = "PUT";
  opts.data = body;
  opts.httpProgress = httpProgress;
  if (accessToken) {
    opts.headers = {
      Authorization: `Bearer ${accessToken}`,
    };
  }
  const res = await xhrAsync(uri, opts);
  return res;
}

export async function throwIfXhrFailed(xhr: XMLHttpRequest) {
  if (xhr.status >= 200 && xhr.status < 300) {
    return;
  }
  //let s = await rsp.text();
  //let s = await rsp.json();
  //console.log("s:", s);
  const msg = `${xhr.responseURL} failed with ${xhr.status} ${xhr.statusText}.`;
  throw new Error(msg);
}

export function mkURL(url: string, ...searchParams: string[]): string {
  let n = len(searchParams);
  if (n == 0) {
    return url;
  }
  throwIf(n % 2 != 0, "must have even number of args");
  n = n / 2;
  url = url + "?";
  for (let i = 0; i < n; i++) {
    if (i > 0) {
      url += "&";
    }
    let idx = i * 2;
    let k = searchParams[idx];
    let v = searchParams[idx + 1];
    url = url + `${k}=${enc(v)}`;
  }
  return url;
}
